import Wrap, { ProductDetails } from './styles';

import logger from 'utils/logger';

import axios from 'axios';
import CenterWrap from 'components/CenterWrap';
import FullScreenLoading from 'components/FullScreenLoading';
import SelectField from 'components/SelectField';
import { useFormik } from 'formik';
import React, { useReducer, useEffect, useState } from 'react';

/**
 * Função auxiliar para ordenação alfabética
 * @param {Object} a - Primeiro item
 * @param {Object} b - Segundo item
 * @param {string} prop - Propriedade para ordenar
 * @returns {number} -1, 0 ou 1 para ordenação
 */
const sort = (a, b, prop) => {
  if (a[prop] < b[prop]) return -1;
  if (a[prop] > b[prop]) return 1;
  return 0;
};

/**
 * Estado inicial do simulador
 */
const initialState = {
  brands: [],
  brand: null,
  models: [],
  model: null,
  products: [],
  product: null,
};

/**
 * Reducer para gerenciar estado do simulador
 * @param {Object} state - Estado atual
 * @param {Object} action - Ação a executar
 * @returns {Object} Novo estado
 */
const reducer = (state, action) => {
  switch (action.type) {
    case 'loadBrandsAndProducts': {
      const { brands, products } = action.payload;
      return { ...state, brands, products };
    }
    case 'loadModels': {
      const { models } = action.payload;
      return { ...state, models };
    }
    case 'resetBrand': {
      return { ...state, models: [], model: null, products: [] };
    }
    case 'setModel': {
      const { model } = action.payload;
      return { ...state, model, products: [] };
    }
    case 'loadProducts': {
      const { products } = action.payload;
      return { ...state, products };
    }
    default:
      logger.debug('Reducer: ação não reconhecida', { type: action.type });
      return state;
  }
};

/**
 * Componente Simulador de Preços
 *
 * REGRAS DE NEGÓCIO:
 *
 * 1. FLUXO DE SELEÇÃO:
 *    - Usuário seleciona marca → carrega modelos da marca
 *    - Usuário seleciona modelo → carrega produtos com preços
 *    - Preços variam por tamanho do veículo (size.id)
 *
 * 2. CÁLCULO DE PREÇOS:
 *    - Preços armazenados em centavos no backend
 *    - Conversão: amount / 100 para exibir em reais
 *    - Cada modelo tem um tamanho (P, M, G) que afeta o preço
 *
 * 3. CATEGORIZAÇÃO:
 *    - Marcas e modelos ordenados alfabeticamente
 *    - Produtos agrupados por categoria de serviço
 *
 * 4. API GRAPHQL:
 *    - Endpoint único com queries específicas
 *    - Dados cacheados no estado para evitar re-fetching
 *
 * @component
 * @returns {JSX.Element[]} Array com loading screen e conteúdo
 */
export default () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);

  /**
   * Carrega modelos de uma marca específica
   * @param {string} brandId - ID da marca selecionada
   */
  const loadModels = brandId => {
    setLoading(true);
    logger.info('Carregando modelos', { brandId });

    axios
      .post(`${process.env.REACT_APP_API_URL}`, {
        query: `{ carModels(brandId:${brandId}){ id, name, size{ id, alias, name } } }`,
      })
      .then(({ data: { data } }) => {
        const models = data.carModels
          .map(model => ({
            ...model,
            size: model.size,
          }))
          .sort((a, b) => sort(a, b, 'name'));
        logger.debug('Modelos carregados', { count: models.length });
        dispatch({
          type: 'loadModels',
          payload: { models },
        });
      })
      .catch(err => {
        logger.error('Erro ao carregar modelos', err);
      })
      .finally(() => setLoading(false));
  };

  /**
   * Define modelo selecionado e seus metadados
   * @param {string} modelId - ID do modelo selecionado
   */
  const setModel = modelId => {
    const model = state.models.find(model => model.id === Number(modelId));

    if (model) {
      if (!model.size) {
        logger.warn('Modelo selecionado sem tamanho definido', {
          modelId,
          model: model.name,
        });
        dispatch({ type: 'setModel', payload: { model } });
        return;
      }

      logger.info('Modelo selecionado', {
        model: model.name,
        size: model.size?.alias || model.size?.name,
      });
      dispatch({ type: 'setModel', payload: { model } });
    }
  };

  /**
   * Define produto selecionado com preço calculado
   * @param {string} productId - ID do produto selecionado
   */
  const setProductState = productId => {
    const product = state.products.find(item => {
      const id = item?.product?.id ?? item?.id;
      return id === Number(productId);
    });

    if (product) {
      const price =
        product.displayPrice ??
        (product.amount != null ? product.amount / 100 : product.price);
      logger.info('Produto selecionado', {
        product: product.product?.name || product.name,
        price,
      });
      setProduct(product);
    }
  };

  /**
   * Carrega marcas e produtos ao montar componente
   */
  useEffect(() => {
    logger.info('Inicializando simulador');
    setLoading(true);

    axios
      .post(`${process.env.REACT_APP_API_URL}`, {
        query: '{ carBrands{ id, name } products{ id, name, description } }',
      })
      .then(response => {
        const brands = response.data.data.carBrands.sort((a, b) =>
          sort(a, b, 'name')
        );
        const products = response.data.data.products
          .sort((a, b) => sort(a, b, 'name'))
          .map(product => ({
            product,
            amount: null,
            price: null,
            displayPrice: null,
          }));

        logger.info('Dados iniciais carregados', {
          brands: brands.length,
          products: products.length,
        });

        dispatch({
          type: 'loadBrandsAndProducts',
          payload: { brands, products },
        });
      })
      .catch(err => {
        logger.error('Erro ao carregar dados iniciais', err);
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * Carrega catálogo de preços quando modelo é selecionado
   * REGRA: Preços variam por tamanho do veículo
   */
  useEffect(() => {
    if (!state.model) return;

    if (!state.model.size) {
      logger.warn('Catálogo não carregado: modelo sem tamanho', {
        model: state.model.name,
        modelId: state.model.id,
      });
      return;
    }

    setLoading(true);
    logger.info('Carregando preços para modelo', {
      model: state.model.name,
      sizeId: state.model.size.id,
    });

    axios
      .post(`${process.env.REACT_APP_API_URL}`, {
        query: `{
        priceCatalog(carSizeId: ${state.model.size.id}) {
          amount
          carSize {
            alias
          }
          product {
            id
            name
            description
          }
        }
      }`,
      })
      .then(({ data: { data } }) => {
        const products = data.priceCatalog
          .slice()
          .sort((a, b) => sort(a.product || a, b.product || b, 'name'))
          .map(item => {
            const product = item.product || null;

            if (!product) {
              logger.warn('Produto retornado sem payload', { item });
              return null;
            }

            const amount = item.amount ?? null;
            const price = item.price ?? null;
            const displayPrice =
              amount != null
                ? amount / 100
                : price != null
                ? Number(price)
                : null;

            return {
              product,
              amount,
              price,
              displayPrice,
              carSize: item.carSize,
            };
          })
          .filter(Boolean);
        logger.debug('Catálogo de preços carregado', {
          count: products.length,
        });

        dispatch({
          type: 'loadProducts',
          payload: { products },
        });
      })
      .catch(err => {
        logger.error('Erro ao carregar catálogo de preços', err);
      })
      .finally(() => setLoading(false));
  }, [state.model]);

  const formik = useFormik({
    initialValues: {
      brand: null,
      model: null,
      product: null,
    },
  });

  return [
    <FullScreenLoading loading={loading} key="loading-screen" />,
    <CenterWrap key="page">
      <Wrap>
        <h1>Simulador</h1>
        <p>
          O preço de cada um de nossos serviços depende muito do tipo e tamanho
          de carro. Para conhecer cada um de nossos serviços selecione seu carro
          e o serviço que você quer saber mais e fique inteirado de tudo!
        </p>
        <br />

        {/* Seletor de Marca */}
        <SelectField
          label={state.brands?.length ? 'Marca do carro' : 'Carregando...'}
          onChange={e => {
            const value = e.target.value;
            formik.setFieldValue('brand', value);
            formik.setFieldValue('model', null);
            formik.setFieldValue('product', null);
            setProduct(null);
            dispatch({ type: 'resetBrand' });
            loadModels(value);
          }}
          onBlur={formik.handleBlur}
          value={formik.values.brand}
          name="brand"
          formikProps={formik}
          width={100}
          topLeftCornerRadius
          topRightCornerRadius
          bottomLeftCornerRadius
          bottomRightCornerRadius
          disabled={false}
        >
          <option value="" />
          {state?.brands &&
            state.brands.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
        </SelectField>
        <br />
        <br />

        {/* Seletor de Modelo - só aparece após selecionar marca */}
        {state.models?.length ? (
          <SelectField
            label="Modelo do carro"
            onChange={e => {
              const value = e.target.value;
              formik.setFieldValue('model', value);
              formik.setFieldValue('product', null);
              setProduct(null);
              setModel(value);
            }}
            onBlur={formik.handleBlur}
            value={formik.values.model}
            name="model"
            formikProps={formik}
            width={100}
            topLeftCornerRadius
            topRightCornerRadius
            bottomLeftCornerRadius
            bottomRightCornerRadius
            disabled={false}
          >
            <option value="" />
            {state?.models &&
              state.models.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
          </SelectField>
        ) : null}
        <br />
        <br />

        {/* Seletor de Produto - só aparece após selecionar modelo */}
        {state.products?.length && state.model ? (
          <SelectField
            label="Produtos"
            onChange={e => {
              const value = e.target.value;
              formik.setFieldValue('product', value);
              setProductState(value);
            }}
            onBlur={formik.handleBlur}
            value={formik.values.product}
            name="product"
            formikProps={formik}
            width={100}
            topLeftCornerRadius
            topRightCornerRadius
            bottomLeftCornerRadius
            bottomRightCornerRadius
            disabled={false}
          >
            <option value="" />
            {state?.products &&
              state.products.map(item => {
                const optionProduct = item.product || item;
                return (
                  <option key={optionProduct?.id} value={optionProduct?.id}>
                    {optionProduct?.name || 'Produto'}
                  </option>
                );
              })}
          </SelectField>
        ) : null}

        {/* Detalhes do Produto - exibe preço e descrição */}
        {product ? (
          <ProductDetails>
            <h1>{product.product?.name}</h1>
            <p>{product.product?.description}</p>
            <div>
              {product.displayPrice != null
                ? `R$ ${product.displayPrice.toFixed(2)}`
                : 'Preço indisponível'}
            </div>
          </ProductDetails>
        ) : null}
      </Wrap>
    </CenterWrap>,
  ];
};
